import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Package, 
  TrendingDown, 
  Rocket, 
  CheckCircle, 
  Clock,
  Star,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { logger } from "@/lib/logger";
import type { Supplier } from "@/types/supplier";

// Feature flag: Set to false to disable signups and show waitlist instead
const SIGNUPS_ENABLED = import.meta.env.VITE_SIGNUPS_ENABLED !== 'false';

export default function Index() {
  const navigate = useNavigate();
  const [featuredSuppliers, setFeaturedSuppliers] = useState<Supplier[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [session, setSession] = useState<{ user?: { id: string } } | null>(null);

  useEffect(() => {
    checkSessionAndRedirect();
    loadFeaturedSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to check session and load suppliers
  }, []);

  const checkSessionAndRedirect = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      // User is logged in - check if they're trying to access a specific portal
      // CRITICAL FIX: Don't redirect if user is already on a portal route
      // This allows multi-role users to access buyer/supplier portals directly
      const currentPath = window.location.pathname;
      const isOnBuyerRoute = currentPath.startsWith('/buyer/');
      const isOnSupplierRoute = currentPath.startsWith('/supplier/');
      const isOnAdminRoute = currentPath.startsWith('/admin/');
      
      // If user is already on a portal route, don't redirect - let ProtectedRoute handle it
      if (isOnBuyerRoute || isOnSupplierRoute || isOnAdminRoute) {
        setSession(session);
        return;
      }
      
      // User is logged in - redirect to appropriate dashboard
      // BUT: Check localStorage first to respect admin acting roles
      const storedRole = localStorage.getItem('holescale_active_role') as string | null;
      
      // Get roles from database
      const { data: rolesData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      const dbRoles = rolesData?.map(r => r.role) || [];
      const isAdmin = dbRoles.includes("admin");

      // CRITICAL: If admin is acting as buyer/supplier, respect that choice and navigate there
      // This must happen BEFORE any other redirect logic
      if (isAdmin && storedRole && (storedRole === 'buyer' || storedRole === 'supplier')) {
        logger.info('Index: Admin acting as role, redirecting to portal', { storedRole });
        navigate(`/${storedRole}/dashboard`, { replace: true });
        return;
      }

      // Otherwise, determine redirect based on roles
      // SECURITY FIX: Use navigate() and remove localStorage manipulation
      // Priority: Admin > Supplier > Buyer
      // CRITICAL: Admin role always takes priority - ensure localStorage is synced
      if (dbRoles.includes("admin")) {
        // Only set to admin if not already set to buyer/supplier (shouldn't happen, but safety check)
        if (!storedRole || storedRole === 'admin') {
          localStorage.setItem('holescale_active_role', 'admin');
        }
        navigate("/admin/dashboard", { replace: true });
        return;
      } else if (dbRoles.includes("supplier")) {
        localStorage.setItem('holescale_active_role', 'supplier');
        navigate("/supplier/dashboard", { replace: true });
        return;
      } else if (dbRoles.includes("buyer")) {
        localStorage.setItem('holescale_active_role', 'buyer');
        navigate("/buyer/dashboard", { replace: true });
        return;
      } else {
        navigate("/auth", { replace: true });
        return;
      }
    }
    
    setSession(session);
  };

  const loadFeaturedSuppliers = async () => {
    // Get suppliers with reviews for better quality
    const { data: suppliersWithRoles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "supplier")
      .limit(6);

    if (!suppliersWithRoles || suppliersWithRoles.length === 0) return;

    const { data } = await supabase
      .from("public_profiles")
      .select("id, company_name, company_logo_url, company_tagline, avatar_url")
      .in("id", suppliersWithRoles.map(s => s.user_id))
      .not("company_name", "is", null)
      .limit(6);

    setFeaturedSuppliers(data || []);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => 
      prev === Math.max(0, featuredSuppliers.length - 4) ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => 
      prev === 0 ? Math.max(0, featuredSuppliers.length - 4) : prev - 1
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-8">
            <div 
              className="cursor-pointer text-2xl font-bold text-primary transition-opacity hover:opacity-80"
              onClick={() => navigate("/")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate("/");
                }
              }}
            >
              HoleScale.
            </div>
            <div className="hidden gap-6 md:flex">
              <Button variant="ghost" onClick={() => navigate("/")}>Home</Button>
              <Button variant="ghost" onClick={() => navigate(session ? "/buyer/suppliers" : "/auth?redirect=/buyer/suppliers")}>Browse Suppliers</Button>
              <Button variant="ghost">How It Works</Button>
              <Button variant="ghost">Pricing</Button>
            </div>
          </div>
          <div className="flex gap-4">
            {session ? (
              <Button size="default" onClick={() => navigate("/buyer/dashboard")}>
                Go to Dashboard →
              </Button>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="default" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const hostname = window.location.hostname;
                    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                    
                    logger.info('Login button clicked', { 
                      currentUrl: window.location.href,
                      origin: window.location.origin,
                      hostname,
                      pathname: window.location.pathname,
                      isLocalhost
                    });
                    
                    // CRITICAL: Force localhost navigation - prevent any redirects
                    if (isLocalhost) {
                      // Use window.location.pathname to ensure we stay on localhost
                      // This prevents any React Router or other redirects
                      const targetPath = "/auth";
                      const fullUrl = `${window.location.protocol}//${window.location.host}${targetPath}`;
                      
                      logger.info('Navigating to auth on localhost', { fullUrl, targetPath });
                      
                      // Try React Router first (preferred)
                      try {
                        navigate(targetPath, { replace: false });
                      } catch (err) {
                        logger.error('React Router navigate failed, using window.location', { error: err });
                        // Fallback: direct navigation but stay on localhost
                        window.location.pathname = targetPath;
                      }
                    } else {
                      // Not localhost - use normal navigation
                      navigate("/auth");
                    }
                  }}
                >
                  Login
                </Button>
                {!SIGNUPS_ENABLED ? (
                  <Button variant="default" size="lg" className="font-semibold" onClick={() => navigate("/waitlist")}>
                    Join Waitlist
                  </Button>
                ) : (
                  <Button variant="default" size="lg" className="font-semibold shadow-md" onClick={() => navigate("/auth")}>
                    Sign Up Free
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Welcome Banner for Logged-in Users */}
      {session && (
        <div className="border-b bg-primary/10 px-6 py-3 text-center">
          <p className="text-sm">
            Welcome back! 
            <Button variant="link" onClick={() => navigate("/buyer/dashboard")}>
              Go to Dashboard →
            </Button>
          </p>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[500px] overflow-hidden bg-black md:min-h-[600px] lg:min-h-[700px]">
        <div className="mx-auto size-full max-w-7xl px-6 md:px-8 lg:px-12">
          <div className="grid min-h-[500px] grid-cols-1 items-center gap-8 md:min-h-[600px] lg:min-h-[700px] lg:grid-cols-2">
            {/* Content - Left side */}
            <div className="z-10 py-12 md:py-16 lg:py-24">
              <h1 className="mb-4 text-3xl font-bold leading-tight text-white md:mb-6 md:text-4xl lg:text-5xl xl:text-6xl">
                Source Custom Packaging Materials at Wholesale Prices
              </h1>
              <p className="mb-6 max-w-lg text-base text-gray-300 md:mb-8 md:text-lg lg:text-xl">
                Connect with 500+ verified packaging suppliers across North America. Corrugated boxes, mailers, labels, food packaging, and more.
              </p>
              <div className="flex flex-col space-y-4 sm:flex-row sm:gap-4 sm:space-y-0">
                {!session && SIGNUPS_ENABLED ? (
                  <>
                    <Button 
                      size="lg" 
                      variant="default"
                      className="w-full bg-white px-8 text-base font-semibold text-black shadow-lg hover:bg-white/90 sm:w-auto"
                      onClick={() => navigate("/auth?mode=signup&type=buyer")}
                    >
                      Sign Up Free — Get Quotes
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="w-full border-white text-white hover:bg-white/10 sm:w-auto"
                      onClick={() => navigate("/auth?mode=signup&type=supplier")}
                    >
                      Join as Supplier
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      size="lg" 
                      variant="default"
                      className="w-full sm:w-auto"
                      onClick={() => navigate(session ? "/buyer/rfq/new" : "/auth?redirect=rfq")}
                    >
                      Post Free RFQ
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="w-full border-white text-white hover:bg-white/10 sm:w-auto"
                      onClick={() => navigate(session ? "/buyer/suppliers" : "/auth?redirect=suppliers")}
                    >
                      Browse 500+ Verified Suppliers
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {/* Product Image - Right side with shadow */}
            <div className="relative flex items-center justify-center py-8 lg:justify-end lg:py-0">
              <img
                src="/images/hero-product.png"
                alt="Custom packaging materials including corrugated boxes, mailers, and shipping supplies"
                width={800}
                height={600}
                className="h-auto w-full max-w-md object-contain drop-shadow-[0_20px_50px_rgba(255,255,255,0.15)] md:max-w-lg lg:w-[120%] lg:max-w-none lg:drop-shadow-[0_25px_60px_rgba(255,255,255,0.2)] xl:w-[130%]"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-card px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-12 text-center text-3xl font-bold">How It Works</h2>
          <div className="grid gap-8 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
                  <Package className="size-8 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold">Post Your Requirements</h3>
                <p className="text-muted-foreground">Describe your packaging needs — corrugated boxes, mailers, poly bags, labels, food packaging, or custom solutions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
                  <TrendingDown className="size-8 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold">Receive Competitive Bids</h3>
                <p className="text-muted-foreground">Get quotes from 5-10 pre-vetted packaging suppliers in 24-48 hours</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
                  <CheckCircle className="size-8 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold">Choose & Order</h3>
                <p className="text-muted-foreground">Compare pricing, lead times, certifications, and supplier capabilities side-by-side</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why HoleScale */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="mb-12 text-center text-3xl font-bold">Why HoleScale</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="mb-3 text-4xl">💰</div>
                <h3 className="mb-2 text-lg font-semibold">20-40% Savings on Packaging Materials</h3>
                <p className="text-sm text-muted-foreground">Competitive bidding drives prices down</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="mb-3 text-4xl">🚀</div>
                <h3 className="mb-2 text-lg font-semibold">Low MOQs Starting at 100 Units</h3>
                <p className="text-sm text-muted-foreground">Start small, scale as you grow</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="mb-3 text-4xl">✅</div>
                <h3 className="mb-2 text-lg font-semibold">Vetted Suppliers</h3>
                <p className="text-sm text-muted-foreground">Every packaging supplier vetted, reviewed, and quality-checked</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="mb-3 text-4xl">⏱️</div>
                <h3 className="mb-2 text-lg font-semibold">24-Hour Quote Response</h3>
                <p className="text-sm text-muted-foreground">Fast response, transparent timelines</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Suppliers */}
      {featuredSuppliers.length > 0 && (
        <section className="bg-card px-6 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mb-12 flex items-center justify-between">
              <h2 className="text-3xl font-bold">Featured Suppliers</h2>
              <Button 
                variant="outline" 
                size="default"
                onClick={() => navigate(session ? "/buyer/suppliers" : "/auth?redirect=/buyer/suppliers")}
              >
                See All Suppliers →
              </Button>
            </div>
            <div className="relative">
              <div className="overflow-hidden">
                <div 
                  className="flex gap-6 transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 25}%)` }}
                >
                  {featuredSuppliers.map((supplier) => (
                    <Card 
                      key={supplier.id} 
                      className="min-w-[calc(25%-18px)] cursor-pointer transition-shadow hover:shadow-lg"
                      onClick={() => {
                        if (!supplier?.id) {
                          logger.error("Cannot navigate: missing supplier id", { supplier });
                          return;
                        }
                        navigate(`/supplier/${supplier.id}`);
                      }}
                    >
                      <CardContent className="pt-6 text-center">
                        <div className="mx-auto mb-4 flex size-20 items-center justify-center rounded-full bg-accent">
                          {supplier.company_logo_url ? (
                            <img
                              src={supplier.company_logo_url}
                              alt={supplier.company_name} 
                              width={64}
                              height={64}
                              className="size-16 rounded-full object-cover" 
                            />
                          ) : (
                            <Package className="size-10 text-muted-foreground" />
                          )}
                        </div>
                        <h3 className="mb-2 font-semibold">{supplier.company_name || "Supplier"}</h3>
                        {supplier.location && (
                          <p className="mb-2 text-xs text-muted-foreground">{supplier.location}</p>
                        )}
                        <Button variant="outline" size="sm" className="mt-2 w-full">
                          View Profile
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              {featuredSuppliers.length > 4 && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute left-0 top-1/2 -translate-x-4 -translate-y-1/2 bg-background/95 shadow-lg backdrop-blur"
                    onClick={prevSlide}
                    aria-label="Previous suppliers"
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-background/95 shadow-lg backdrop-blur"
                    onClick={nextSlide}
                    aria-label="Next suppliers"
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Social Proof */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-4xl text-center">
          <p className="mb-8 text-lg text-muted-foreground">Trusted by 50+ e-commerce brands</p>
          <Card className="bg-accent/50">
            <CardContent className="pt-6">
              <div className="mb-4 flex justify-center">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="size-5 fill-warning text-warning" />
                ))}
              </div>
              <p className="mb-4 text-lg italic">
                "HoleScale helped us find the perfect packaging supplier. We saved 35% compared to our previous supplier and the quality is outstanding!"
              </p>
              <p className="font-semibold">Sarah Johnson</p>
              <p className="text-sm text-muted-foreground">CEO, EcoBox Co.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-secondary/20 to-primary/20 px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mb-6 text-4xl font-bold">Ready to Save on Packaging Materials?</h2>
          <p className="mb-8 text-xl text-muted-foreground">
            Join hundreds of brands finding better deals on corrugated boxes, mailers, labels, and custom packaging
          </p>
          <div className="mx-auto max-w-md px-4">
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Button 
                size="lg" 
                variant="default"
                className="w-full sm:w-auto"
                onClick={() => navigate("/auth")}
              >
                Post Free RFQ
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="w-full sm:w-auto"
                onClick={() => navigate(session ? "/buyer/suppliers" : "/auth?redirect=/buyer/suppliers")}
              >
                Browse 500+ Verified Suppliers
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card px-6 py-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 grid gap-8 md:grid-cols-4">
            <div>
              <div 
                className="mb-4 cursor-pointer text-lg font-bold transition-opacity hover:opacity-80"
                onClick={() => navigate("/")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate("/");
                  }
                }}
              >
                HoleScale.
              </div>
              <p className="text-sm text-muted-foreground">
                The leading B2B marketplace for packaging materials. Connecting businesses with verified suppliers of corrugated boxes, mailers, labels, food packaging, and more.
              </p>
            </div>
            <div>
              <h4 className="mb-3 font-semibold">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/about" className="hover:text-foreground">About</a></li>
                <li><a href="/contact" className="hover:text-foreground">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 font-semibold">Resources</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/how-it-works" className="hover:text-foreground">How It Works</a></li>
                <li><a href="/pricing" className="hover:text-foreground">Pricing</a></li>
                <li><a href="/faq" className="hover:text-foreground">FAQ</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-3 font-semibold">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/terms" className="hover:text-foreground">Terms</a></li>
                <li><a href="/privacy" className="hover:text-foreground">Privacy</a></li>
                <li><a href="/cookies" className="hover:text-foreground">Cookies</a></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-4 border-t pt-8 md:flex-row">
            <p className="text-sm text-muted-foreground">
              © 2025 HoleScale. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href="https://www.linkedin.com/company/holescale" target="_blank" rel="noopener noreferrer" aria-label="HoleScale on LinkedIn" className="text-muted-foreground hover:text-foreground">
                <svg className="size-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
